import { ThemeEvents } from '@theme/events';

/**
 * "Qual meu tamanho?" e o link "Dúvida no tamanho? Fale com uma vendedora" da página do produto (blocks/size-guide).
 *
 * A conversão é a mesma da vendedora do CRM (CRM CAPIM SANTO, functions/src/seller-size.ts, 26/10/2026):
 * - peso para 1,58–1,70 m, e a altura ajusta a faixa (referência de manequim por peso, não é dado nosso);
 * - número de roupa pelo quadril do guia × manequim de referência;
 * - busto, cintura e quadril, quando ela informa, decidem pelo guia de medidas do site (dado nosso).
 * Entre dois tamanhos, fora da grade, tamanho que a peça não tem ou esgotado na cor: manda para o WhatsApp em vez de
 * escolher por ela. Mudar uma faixa aqui exige mudar o seller-size.ts junto (e o contrário).
 *
 * Todas as grades respondem (27/10/2026, pedido do dono: "a calculadora precisa funcionar em todos os produtos"):
 * - numeração (36/38/40): o número que ela veste é o da peça; sem número, o peso vira número pelo inverso do
 *   SIZE_BY_NUMBER do CRM (P = 34/36, M = 40, G = 44, GG = 48/50, e a divisa entre duas letras é o número do meio:
 *   P/M = 38, M/G = 42, G/GG = 46). O seller-size.ts não tem a ida de letra para número; esta é a volta exata da tabela
 *   dele, então os dois não se contradizem;
 * - tamanho único: diz que é tamanho único e oferece a vendedora. Nenhuma fonte tem a faixa de medidas dessas peças,
 *   então nada é inventado;
 * - grade parcial (P/M, M/G/GG, P/G…): se o indicado não existe, diz o vizinho que existe (mais justo ou mais folgado)
 *   e manda confirmar com a vendedora; dois passos ou mais, fora da grade.
 */

const ORDER = ['P', 'M', 'G', 'GG'];
const SIZE_BY_WEIGHT = [
  [54, { size: 'P' }], [59, { between: ['P', 'M'] }], [66, { size: 'M' }], [71, { between: ['M', 'G'] }],
  [80, { size: 'G' }], [86, { between: ['G', 'GG'] }], [95, { size: 'GG' }],
];
const ONE_SIZE = /^(u|un|unico|único|tu)$/i;
const NUMBERS = ['34', '36', '38', '40', '42', '44', '46', '48', '50'];
/** Inverso do SIZE_BY_NUMBER: tamanho (ou divisa) → números de roupa. */
const NUMBERS_BY_BAND = { P: ['36', '34'], 'P-M': ['38'], M: ['40'], 'M-G': ['42'], G: ['44'], 'G-GG': ['46'], GG: ['48', '50'] };
const SIZE_BY_NUMBER = {
  34: { size: 'P' }, 36: { size: 'P' }, 38: { between: ['P', 'M'] }, 40: { size: 'M' }, 42: { between: ['M', 'G'] },
  44: { size: 'G' }, 46: { between: ['G', 'GG'] }, 48: { size: 'GG' }, 50: { size: 'GG' },
};

/** Altura muda a faixa: mais baixa, o mesmo peso pede tamanho maior; mais alta, menor. */
function effectiveWeight(kg, heightM) {
  if (!heightM) return kg;
  if (heightM < 1.58) return kg + 4;
  if (heightM >= 1.76) return kg - 8;
  if (heightM > 1.7) return kg - 5;
  return kg;
}
function bandForWeight(kg, heightM) {
  const weight = effectiveWeight(kg, heightM);
  return SIZE_BY_WEIGHT.find(([limit]) => weight <= limit)?.[1] ?? { above: true };
}
function bandForNumber(number) {
  if (number < 34) return { size: 'P' };
  if (number > 50) return { above: true };
  return SIZE_BY_NUMBER[number] ?? SIZE_BY_NUMBER[number + 1];
}
/** "P:82-88,M:89-94" → tamanho da medida pelo guia (meio centímetro de folga nas pontas); fora do guia, fora da grade. */
function bandForMeasure(value, ranges) {
  const rows = ranges.split(',').map((row) => {
    const [size, range] = row.split(':');
    const [low, high] = range.split('-').map(Number);
    return { size, low: low - 0.5, high: high + 0.5 };
  });
  if (value < rows[0].low) return { below: true };
  const row = rows.find((item) => value >= item.low && value <= item.high);
  return row ? { size: row.size } : { above: true };
}
/** "62", "62,5", "1,64", "164" → número; vazio → undefined. */
function readNumber(text) {
  const clean = String(text || '').trim().replace(/\s/g, '').replace(',', '.').replace(/[^\d.]/g, '');
  if (!clean) return undefined;
  const value = Number(clean);
  return Number.isFinite(value) ? value : NaN;
}
const brText = (value) => String(value).replace('.', ',');
const sizeWord = (size) => `o ${size}`;
const humanList = (items) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}` : items[0] || '');
const bandKey = (band) => (band.size ? band.size : band.between ? band.between.join('-') : '');

class CapimTamanho extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-capim-tamanho]');
    if (!data) return;
    this.data = JSON.parse(data.textContent);
    this.variantId = this.data.variant;
    this.answer = '';
    this.section = this.closest('.shopify-section') || document;
    this.form = this.querySelector('[data-secao="calculadora"]');
    this.result = this.querySelector('[data-resultado]');

    this.form?.addEventListener('submit', this.onSubmit);
    this.form?.addEventListener('click', this.onResultClick);
    this.addEventListener('click', this.onOpen);
    this.section.addEventListener(ThemeEvents.variantUpdate, this.onVariant);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariant);
  }

  /** "Tabela de medidas" abre o mesmo modal já na tabela; "Qual meu tamanho?", no começo. */
  onOpen = (event) => {
    const trigger = event.target.closest?.('[data-abrir]');
    if (!trigger) return;
    const target = this.querySelector(`[data-secao="${trigger.dataset.abrir}"]`);
    const body = this.querySelector('[data-corpo]');
    requestAnimationFrame(() => {
      if (trigger.dataset.abrir === 'tabela' && target && body) body.scrollTop = target.offsetTop - body.offsetTop;
      else if (body) body.scrollTop = 0;
    });
  };

  onVariant = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;
    this.variantId = Number(variant.id);
    this.updateLinks();
  };

  currentVariant() {
    return this.data.variants.find((variant) => variant.id === Number(this.variantId)) || this.data.variants[0];
  }

  currentColor() {
    if (this.data.colorIndex < 0) return '';
    return this.currentVariant()?.options[this.data.colorIndex] || '';
  }

  /** O texto do contrato com o CRM; a 2ª linha só quando ela usou o "Qual meu tamanho?". */
  whatsappUrl() {
    const color = this.currentColor();
    let message = `Oi! Tenho dúvida no tamanho: ${this.data.title}${color ? ` – cor ${color}` : ''} – ${this.data.url}`;
    if (this.answer) message += `\n${this.answer}`;
    return `https://wa.me/${this.data.phone}?text=${encodeURIComponent(message)}`;
  }

  updateLinks() {
    const url = this.whatsappUrl();
    this.section.querySelectorAll('[data-duvida-tamanho]').forEach((link) => link.setAttribute('href', url));
  }

  onSubmit = (event) => {
    event.preventDefault();
    const read = (name) => readNumber(this.form.elements.namedItem(name)?.value);
    const body = {
      kg: read('peso'), heightM: read('altura'), number: read('numero'),
      busto: read('busto'), cintura: read('cintura'), quadril: read('quadril'),
    };
    if (body.heightM && body.heightM > 3) body.heightM = body.heightM / 100;

    const problem = this.validate(body);
    if (problem) return this.show({ text: problem });

    const outcome = this.data.grid === 'numbers' ? this.byNumberGrid(body) : this.data.grid === 'one' ? this.byOneSize(body) : this.byLetters(body);
    this.answer = this.describe(body, outcome.summary);
    this.updateLinks();
    this.show(outcome);
  };

  validate(body) {
    const bad = (value, min, max) => value !== undefined && (Number.isNaN(value) || value < min || value > max);
    if (bad(body.kg, 35, 160)) return 'Confira o peso: use só o número, em quilos (por exemplo, 62).';
    if (bad(body.heightM, 1.4, 1.95)) return 'Confira a altura: por exemplo, 1,64.';
    if (bad(body.number, 30, 60)) return 'Confira o número que você veste: por exemplo, 40.';
    if (bad(body.busto, 60, 150) || bad(body.cintura, 50, 140) || bad(body.quadril, 70, 170)) {
      return 'Confira as medidas: são em centímetros, por exemplo busto 90 e quadril 100.';
    }
    const hasMeasure = body.busto || body.cintura || body.quadril;
    if (this.data.grid === 'one') return '';
    if (this.data.grid === 'numbers' && !body.number && !body.kg) return 'Preencha o número que você veste ou o seu peso.';
    if (!body.kg && !body.number && !hasMeasure) return 'Preencha o seu peso ou o número que você veste.';
    return '';
  }

  /** Peça vendida por numeração (36, 38, 40): o número dela é o da peça; sem número, o peso vira número. */
  byNumberGrid(body) {
    const offered = this.data.sizes;
    if (body.number) {
      let number = Math.round(body.number);
      if (number % 2) number += 1;
      const wanted = String(number);
      if (!offered.includes(wanted)) return this.missing(wanted, NUMBERS, offered);
      return this.offer(wanted, `Você veste ${wanted}: escolha o <strong>${wanted}</strong>.`, 'Pelo número que você veste.');
    }
    const band = bandForWeight(body.kg, body.heightM);
    if (band.above) return this.outOfGrid(offered);
    const candidates = NUMBERS_BY_BAND[bandKey(band)];
    const wanted = candidates.find((number) => offered.includes(number)) || candidates[0];
    if (!offered.includes(wanted)) return this.missing(wanted, NUMBERS, offered);
    let how = `É uma indicação pela tabela da loja (peso para 1,58 a 1,70 m${body.heightM ? ', ajustado pela sua altura' : ''}), não uma garantia.`;
    if (band.size === 'P' && wanted === '36' && !offered.includes('34')) how += ' Quem usa 34 também fica no 36, o menor desta peça.';
    return this.offer(wanted, `Pela nossa tabela, o <strong>${wanted}</strong> costuma ficar bem em você.`, how);
  }

  /** Tamanho único: não há o que escolher; sem medidas publicadas da peça, a vendedora confirma. */
  byOneSize(body) {
    const band = body.kg ? bandForWeight(body.kg, body.heightM) : body.number ? bandForNumber(Math.round(body.number)) : null;
    const hint = !band ? '' : band.size ? band.size : band.between ? `entre o ${band.between[0]} e o ${band.between[1]}` : 'acima do GG';
    const color = this.currentColor();
    const variant = this.data.variants.find((item) => this.data.colorIndex < 0 || item.options[this.data.colorIndex] === color);
    const soldOut = variant && !variant.available;
    return {
      summary: `tamanho único${hint ? ` (pela tabela, ${hint})` : ''}${soldOut ? `, esgotado na cor ${color}` : ''}`,
      text: soldOut
        ? `Esta peça é <strong>tamanho único</strong>, mas está esgotada${color ? ` na cor ${color}` : ''}: veja outra cor ou fale com uma vendedora.`
        : 'Esta peça é <strong>tamanho único</strong>: vem num tamanho só, não há o que escolher.',
      notes: ['Ainda não publicamos as medidas dela. Para ter certeza de que veste bem em você, uma vendedora confirma pelo WhatsApp com os dados que você informou.'],
      whatsapp: true,
    };
  }

  /** "P e M"; na grade misturada com U (mini-saia G / U), "G e tamanho único". */
  gridText(offered) {
    return this.data.sizes.some((item) => ONE_SIZE.test(item)) ? `${humanList(offered)} e tamanho único` : humanList(offered);
  }

  /** Fora da grade (acima ou abaixo de tudo o que a peça tem). */
  outOfGrid(offered) {
    return {
      summary: 'fora da grade da peça',
      text: `Pelo que você informou, o seu tamanho fica fora da grade desta peça (${this.gridText(offered)}). Uma vendedora te ajuda a escolher a melhor opção.`,
      whatsapp: true,
    };
  }

  /** O indicado não existe na peça: o vizinho que existe, a um passo, mais justo ou mais folgado; longe disso, fora. */
  missing(wanted, scale, offered) {
    const at = scale.indexOf(wanted);
    const near = offered.filter((size) => at >= 0 && Math.abs(scale.indexOf(size) - at) === 1).sort((a, b) => scale.indexOf(a) - scale.indexOf(b));
    const fit = (size) => (scale.indexOf(size) < at ? 'mais justo' : 'mais folgado');
    const intro = `A indicação é o <strong>${wanted}</strong>, e esta peça vem em ${this.gridText(offered)}.`;
    if (!near.length) {
      return { summary: `${wanted}, que esta peça não tem`, text: `${intro} Uma vendedora te mostra opções no seu tamanho.`, whatsapp: true };
    }
    if (near.length === 1) {
      return {
        summary: `${wanted}, que esta peça não tem (o mais próximo é o ${near[0]})`,
        text: `${intro} O mais próximo é ${sizeWord(near[0])}, que tende a ficar ${fit(near[0])}. Antes de escolher, confirme com uma vendedora.`,
        whatsapp: true,
      };
    }
    return {
      summary: `${wanted}, que esta peça não tem (os mais próximos são o ${near[0]} e o ${near[1]})`,
      text: `${intro} Os mais próximos são ${sizeWord(near[0])} (${fit(near[0])}) e ${sizeWord(near[1])} (${fit(near[1])}). Uma vendedora confirma qual veste melhor.`,
      whatsapp: true,
    };
  }

  byLetters(body) {
    const measures = ['busto', 'cintura', 'quadril'].filter((name) => body[name] && this.data.guide[name]);
    const usesGuide = measures.length > 0 && ['biquini', 'maio'].includes(this.data.category);
    const bands = usesGuide
      ? measures.map((name) => bandForMeasure(body[name], this.data.guide[name]))
      : [body.kg ? bandForWeight(body.kg, body.heightM) : null, body.number ? bandForNumber(Math.round(body.number)) : null].filter(Boolean);

    const letters = this.data.sizes.filter((size) => ORDER.includes(size)).sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    if (bands.some((band) => band.above || band.below)) return this.outOfGrid(letters);

    const sizes = [...new Set(bands.flatMap((band) => band.size ? [band.size] : band.between))].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    if (sizes.length === 2 && ORDER.indexOf(sizes[1]) - ORDER.indexOf(sizes[0]) === 1) {
      const inPiece = sizes.filter((size) => letters.includes(size));
      if (inPiece.length === 1) {
        const only = inPiece[0];
        return {
          summary: `entre ${sizeWord(sizes[0])} e ${sizeWord(sizes[1])} (a peça só tem ${sizeWord(only)})`,
          text: `Você fica <strong>entre ${sizeWord(sizes[0])} e ${sizeWord(sizes[1])}</strong>, e desta peça só existe ${sizeWord(only)}, que tende a ficar ${only === sizes[0] ? 'mais justo' : 'mais folgado'}. Uma vendedora confirma pelas suas medidas antes da compra.`,
          whatsapp: true,
        };
      }
      if (!inPiece.length) return this.outOfGrid(letters);
      return {
        summary: `entre ${sizeWord(sizes[0])} e ${sizeWord(sizes[1])}`,
        text: `Você fica <strong>entre ${sizeWord(sizes[0])} e ${sizeWord(sizes[1])}</strong>. Para não errar, uma vendedora confirma pelas suas medidas antes da compra.`,
        whatsapp: true,
      };
    }
    if (sizes.length > 1) {
      return {
        summary: `tamanhos diferentes (${sizes.join(' e ')})`,
        text: `As informações apontam para tamanhos diferentes (${sizes.join(' e ')}). Uma vendedora confirma com você qual escolher.`,
        whatsapp: true,
      };
    }

    const size = sizes[0];
    if (!letters.includes(size)) return this.missing(size, ORDER, letters);
    let how = 'Pela tabela de medidas da Capim Santo.';
    if (!usesGuide) {
      how = body.kg
        ? `É uma indicação pela tabela da loja (peso para 1,58 a 1,70 m${body.heightM ? ', ajustado pela sua altura' : ''}), não uma garantia.`
        : 'É uma indicação pela tabela da loja, não uma garantia.';
      if (['biquini', 'maio'].includes(this.data.category)) how += ' Com busto e quadril, fica mais preciso.';
    }
    return this.offer(size, `Pela nossa tabela, o <strong>${size}</strong> costuma ficar bem em você.`, how, !body.busto);
  }

  /** Tamanho certo: confere se tem na cor escolhida e oferece selecionar. */
  offer(size, headline, how = '', bustNote = false) {
    const color = this.currentColor();
    const variant = this.data.variants.find((item) =>
      item.options[this.data.sizeIndex] === size && (this.data.colorIndex < 0 || item.options[this.data.colorIndex] === color));
    if (variant && !variant.available) {
      const oneInColor = this.data.variants.some((item) => ONE_SIZE.test(item.options[this.data.sizeIndex]) && item.available
        && (this.data.colorIndex < 0 || item.options[this.data.colorIndex] === color));
      return {
        summary: `${size}, esgotado na cor ${color}`,
        text: `${headline} Mas o ${size} está esgotado${color ? ` na cor ${color}` : ''}${oneInColor ? ', que nesta cor vem em tamanho único (U)' : ''}: veja outra cor ou fale com uma vendedora.`,
        whatsapp: true,
        highlight: size,
      };
    }
    const notes = [how];
    if (this.data.sizes.some((item) => ONE_SIZE.test(item))) notes.push('Esta peça também vem em tamanho único (U) em algumas cores; se quiser o U, uma vendedora confirma se veste bem.');
    if (this.data.category === 'biquini' && bustNote) notes.push('No top do biquíni, quem decide é o busto.');
    if (this.data.category === 'saida') notes.push('Saídas e vestidos têm caimento diferente em cada modelo e esta peça ainda não tem medidas publicadas: na dúvida, confirme com uma vendedora.');
    if (this.data.onSale) notes.push('Peça em promoção: na dúvida, confirme o tamanho com uma vendedora antes de comprar.');
    return { summary: size, text: headline, notes: notes.filter(Boolean), select: size, highlight: size };
  }

  /** A 2ª linha da mensagem do WhatsApp: o que ela informou, nas palavras que o CRM já lê. */
  describe(body, summary) {
    const parts = [];
    if (body.kg) parts.push(`${brText(body.kg)} kg`);
    if (body.heightM) parts.push(`${body.heightM.toFixed(2).replace('.', ',')} m`);
    if (body.number) parts.push(`uso ${Math.round(body.number)}`);
    for (const name of ['busto', 'cintura', 'quadril']) if (body[name]) parts.push(`${name} ${brText(body[name])} cm`);
    if (!parts.length) return '';
    return `Minhas medidas: ${parts.join(', ')} (o site indicou: ${summary})`;
  }

  show(outcome) {
    const lines = [`<p class="size-guide__result-main">${outcome.text}</p>`];
    for (const note of outcome.notes || []) lines.push(`<p class="size-guide__result-note">${note}</p>`);
    const actions = [];
    if (outcome.select) actions.push(`<button type="button" class="button size-guide__result-select" data-escolher="${outcome.select}">Escolher ${outcome.select}</button>`);
    if (outcome.whatsapp || outcome.select) {
      actions.push(`<a class="size-guide__whatsapp${outcome.whatsapp ? ' size-guide__whatsapp--button' : ''}" href="${this.whatsappUrl()}" target="_blank" rel="noopener noreferrer" data-duvida-tamanho>${outcome.whatsapp ? 'Confirmar com uma vendedora' : 'Quer confirmar? Fale com uma vendedora'}</a>`);
    }
    if (actions.length) lines.push(`<div class="size-guide__result-actions">${actions.join('')}</div>`);
    this.result.innerHTML = lines.join('');
    this.result.hidden = false;
    this.result.classList.toggle('size-guide__result--whatsapp', Boolean(outcome.whatsapp));

    this.querySelectorAll('tr[data-tamanho]').forEach((row) => row.classList.toggle('is-indicado', row.dataset.tamanho === outcome.highlight));
    this.result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /** "Escolher M": marca o tamanho no seletor da página e fecha o modal. */
  onResultClick = (event) => {
    const button = event.target.closest?.('[data-escolher]');
    if (!button) return;
    const size = button.dataset.escolher;
    const input = Array.from(this.section.querySelectorAll('variant-picker input[type="radio"]')).find(
      (item) => item.value === size && item.name.startsWith(`${this.data.sizeName}-`),
    );
    if (input && !input.checked) input.click();
    this.querySelector('dialog-component')?.closeDialog?.();
  };
}

if (!customElements.get('capim-tamanho')) {
  customElements.define('capim-tamanho', CapimTamanho);
}
